import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ExternalLink } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@solara/ui-shared';
import { useInvoice, useTenant } from '@solara/api-client/react';
import { adaptInvoiceRow, adaptTenant } from '../data/adapters';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

export default function InvoiceDetailPage() {
  const { t } = useTranslation('admin');
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const invoiceQuery = useInvoice(invoiceId);
  const row = invoiceQuery.data ? adaptInvoiceRow(invoiceQuery.data) : undefined;
  const tenantQuery = useTenant(row?.tenantId);
  const tenant = tenantQuery.data ? adaptTenant(tenantQuery.data) : undefined;
  const { locale } = useI18n();
  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });

  if (invoiceQuery.isLoading) return <LoadingBlock />;
  if (invoiceQuery.error) return <ErrorBanner error={invoiceQuery.error} />;

  if (!row) {
    return (
      <div className="space-y-4">
        <Link to="/invoices" className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg">
          <ArrowLeft className="h-4 w-4 rtl-mirror" /> {t('invoiceDetail.back')}
        </Link>
        <EmptyState icon={<AlertTriangle className="h-6 w-6" />} title="Invoice not found" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link to="/invoices" className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4 rtl-mirror" /> {t('invoiceDetail.back')}
      </Link>

      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{row.invoiceNumber}</h1>
          <div className="text-fg-muted mt-1">
            {t('invoiceDetail.tenant')}:{' '}
            {tenant ? (
              <Link to={`/tenants/${tenant.tenantId}`} className="text-brand-700 underline inline-flex items-center gap-1">
                {tenant.companyNameEn}
                <ExternalLink className="h-3 w-3 rtl-mirror" />
              </Link>
            ) : (
              row.tenantId.slice(0, 8)
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={row.mlsStatus === 'AP' ? 'success' : row.mlsStatus === 'RE' ? 'danger' : 'info'}>
            {row.mlsStatus}
          </Badge>
          <Badge tone={row.direction === 'OUTBOUND' ? 'brand' : 'neutral'}>
            {t(`invoices.direction.${row.direction}`)}
          </Badge>
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-3">
        <Stat label="Gross" value={aedFmt.format(row.totalGrossAmount)} />
        <Stat label="VAT" value={aedFmt.format(row.totalVatAmount)} />
        <Stat label="Issued" value={dateFmt.format(new Date(row.issueDate))} />
      </section>

      <Tabs defaultValue="summary">
        <TabsList>
          <TabsTrigger value="summary">{t('invoiceDetail.tabs.summary')}</TabsTrigger>
          <TabsTrigger value="raw">{t('invoiceDetail.tabs.raw')}</TabsTrigger>
          <TabsTrigger value="audit">{t('invoiceDetail.tabs.audit')}</TabsTrigger>
        </TabsList>
        <TabsContent value="summary">
          <Card>
            <CardHeader>
              <CardTitle>Buyer & metadata</CardTitle>
            </CardHeader>
            <CardBody className="grid gap-4 sm:grid-cols-2 text-sm">
              <Row label="Buyer" value={row.buyerName} />
              <Row label="Currency" value="AED" />
              <Row label="Direction" value={row.direction} />
              <Row label="Tenant TRN" value={<span dir="ltr" className="num">{tenant?.trn ?? '—'}</span>} />
              <Row label="Peppol participant" value={<code className="text-[10px]">{tenant?.peppolId ?? '—'}</code>} />
              <Row label="ERP" value={tenant?.industry ?? '—'} />
            </CardBody>
          </Card>
        </TabsContent>
        <TabsContent value="raw">
          <Card>
            <CardBody>
              <pre className="text-xs overflow-x-auto bg-surface-muted p-4 rounded-lg">
{JSON.stringify(
  {
    invoiceId: row.invoiceId,
    tenantId: row.tenantId,
    invoiceNumber: row.invoiceNumber,
    issueDate: row.issueDate,
    currency: 'AED',
    totalGrossAmount: row.totalGrossAmount,
    vatAmount: row.totalVatAmount,
    mlsStatus: row.mlsStatus,
    direction: row.direction,
  },
  null,
  2,
)}
              </pre>
            </CardBody>
          </Card>
        </TabsContent>
        <TabsContent value="audit">
          <Card>
            <CardBody>
              <p className="text-sm text-fg-muted">
                Per-invoice audit trail merges with cross-tenant audit log under{' '}
                <Link to="/audit" className="text-brand-700 underline">
                  /audit
                </Link>
                .
              </p>
            </CardBody>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardBody className="p-4 space-y-1">
        <div className="text-xs uppercase tracking-wide text-fg-subtle">{label}</div>
        <div className="text-xl font-semibold num">{value}</div>
      </CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-fg-muted">{label}</span>
      <span className="text-end">{value}</span>
    </div>
  );
}
